import { FC, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, Stack } from "@mui/material";
import { useAtom, useSetAtom } from "jotai";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { computeFiscalYears } from "../../../../common/fiscalPeriods";
import ErrorMessage from "../../../../components/common/ErrorMessage";
import FormSectionTitle from "../../../../components/forms/FormSectionTitle";
import FormSelect from "../../../../components/forms/FormSelect";
import FormTextField from "../../../../components/forms/FormTextField";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import {
	CompanyRegistryPayload,
	emptyCompanyRegistryPayload,
	useCompaniesValidationSchemas,
} from "../../../../datasources/requests/adminCompanies";
import { GetOptionsTopic } from "../../../../datasources/requests/common";
import { CompanyRegistry, emptyCompanyRegistry } from "../../../../datasources/responses/adminCompanies";
import { useServerOptions } from "../../../../hooks/useOptions";
import { useResource } from "../../../../hooks/useResource";
import { currentUser } from "../../../../state/auth";
import { userPeriodFilter } from "../../../../state/company";
import FiscalPeriodPicker from "./FiscalDatePicker";

interface Props {
	id: number;
}

const CompanyDetails: FC<Props> = ({ id }) => {
	const { t } = useTranslation("feature.company");
	const { UpdateCompanySchema } = useCompaniesValidationSchemas();
	const { data: countries, isLoading: countriesAreLoading } = useServerOptions(GetOptionsTopic.Countries);
	const initUserPeriodFilter = useSetAtom(userPeriodFilter);
	const [userData, setUserData] = useAtom(currentUser);

	const {
		data: company,
		isLoading: companyIsLoading,
		error,
		put,
		reload,
	} = useResource<CompanyRegistryPayload, CompanyRegistry>(
		{ route: ApiRoutes.AdminCompanies, resourceId: id },
		{ emptyFallback: emptyCompanyRegistry, requireResourceId: true },
		{ subjectKey: "company" }
	);

	const form = useForm<CompanyRegistryPayload>({
		defaultValues: emptyCompanyRegistryPayload,
		resolver: zodResolver(UpdateCompanySchema),
	});

	const isLoading = companyIsLoading || countriesAreLoading;

	const onSubmit = () => {
		const values = form.getValues();
		put(values, {
			onSuccess: (response) => {
				const fiscalPeriodParts = values.fiscalPeriod.split("/");
				const fiscalYears = computeFiscalYears(fiscalPeriodParts[0], fiscalPeriodParts[1]);
				initUserPeriodFilter({
					start: fiscalYears[0].start,
					end: fiscalYears[0].end,
					selectedPeriodId: 0,
					type: "fiscal",
					fiscalYears,
				});
				setUserData({
					...userData!,
					company: {
						id: response!.id,
						name: response!.name,
						fiscalPeriod: response!.fiscalPeriod,
					},
				});
			},
		});
	};

	useEffect(() => {
		form.reset(
			{
				name: company?.name,
				address: company?.address,
				zipCode: company?.zipCode,
				city: company?.city,
				province: company?.province,
				country: company?.country,
				vat: company?.vat,
				fiscalPeriod: company?.fiscalPeriod,
				tripsLimit: company?.tripsLimit,
				nodesLimit: company?.nodesLimit,
				nodesDetailsLimit: company?.nodesDetailsLimit,
				connectionType: company?.connectionType,
				connectionDir: company?.connectionDir,
				connectionSubDir: company?.connectionSubDir,
				dirStructure: company?.dirStructure,
			},
			{ keepErrors: true }
		);
	}, [company, form]);

	if (error) {
		return <ErrorMessage httpStatusCode={error.httpCode} accept={{ onClick: reload }} />;
	}

	return (
		<Grid container spacing={2}>
			<FormSectionTitle title={t("registry")} />

			<Grid size={6}>
				<FormTextField fullWidth isLoading={isLoading} form={form} name="name" label={t("name")} />
			</Grid>
			<Grid size={6}>
				<FormTextField fullWidth isLoading={isLoading} form={form} name="vat" label={t("vat")} />
			</Grid>
			<Grid size={12}>
				<FormTextField
					fullWidth
					isLoading={isLoading}
					form={form}
					name="address"
					label={t("address", { ns: "feature.network" })}
				/>
			</Grid>
			<Grid size={6}>
				<FormTextField
					fullWidth
					isLoading={isLoading}
					form={form}
					name="zipCode"
					label={t("zip code", { ns: "feature.network" })}
				/>
			</Grid>
			<Grid size={6}>
				<FormTextField
					fullWidth
					isLoading={isLoading}
					form={form}
					name="city"
					label={t("city", { ns: "feature.network" })}
				/>
			</Grid>
			<Grid size={6}>
				<FormTextField
					fullWidth
					isLoading={isLoading}
					form={form}
					name="province"
					label={t("province", { ns: "feature.network" })}
				/>
			</Grid>
			<Grid size={6}>
				<FormSelect
					fullWidth
					isLoading={isLoading}
					form={form}
					name="country"
					label={t("country", { ns: "feature.network" })}
					options={countries?.options ?? []}
				/>
			</Grid>
			<Grid size={6} display={"flex"} alignItems={"center"}>
				<FiscalPeriodPicker
					fullWidth={true}
					isLoading={isLoading}
					form={form}
					valueStyle="short"
					name="fiscalPeriod"
				/>
			</Grid>

			<Grid size={12}>
				<Stack direction="row-reverse" spacing={2}>
					<Button className="confirm" loading={isLoading} loadingPosition="start" onClick={onSubmit}>
						{t("apply", { ns: "common" })}
					</Button>
					<Button className="confirm" loading={isLoading} loadingPosition="start" onClick={reload}>
						{t("reload", { ns: "common" })}
					</Button>
				</Stack>
			</Grid>
		</Grid>
	);
};

export default CompanyDetails;
